// Derives the "pizza tracker" stage view from a case's status (+ optional
// audit-mined hints). This is a pure PROJECTION of the Engine's one status
// enum — it holds no state of its own and must never become a second
// status system. Stage boundaries mirror src/engine/state-machine.ts in
// the Engine repo.

import type { CaseStatus } from '../types/case';
import type { AuditEntry } from '../types/dashboard';

// Stage names (Danny, 2026-08-22): Intake → Confirmation →
// Schedule/Pay → Mediation → Agreement → Closed.
export const STAGE_LABELS = [
  'Intake',
  'Confirmation',
  'Schedule/Pay',
  'Mediation',
  'Agreement',
  'Closed',
] as const;

/** -1 = STALLED (position depends on where the case stalled from). */
const STATUS_STAGE: Record<CaseStatus, number> = {
  INTAKE: 0,
  ASSESSING: 1,
  GATHERING: 1,
  EVALUATING: 2,
  NEGOTIATING: 2,
  // Scheduling/payment machinery runs while ESCALATING — that's the
  // Schedule/Pay stage; the case advances to Mediation when a session
  // actually starts (MEDIATING).
  ESCALATING: 2,
  MEDIATING: 3,
  STALLED: -1,
  RESOLVED: 4,
  CLOSED: 5,
};

export interface CaseStage {
  /** 0-based index into STAGE_LABELS of the current stage. */
  index: number;
  label: string;
  /** Extra context under the label: 'scheduling' / 'in session' / 'stalled'. */
  sublabel: string | null;
  stalled: boolean;
  /** Case resolved without a mediation session — render that step skipped,
      not checked (NEGOTIATING → RESOLVED is a legal transition). */
  mediationSkipped: boolean;
}

export interface StageHints {
  /** The status the case stalled from (mined from audit previous_state). */
  stalledFrom?: CaseStatus | null;
  /** Whether a mediation session was ever booked/held. undefined = unknown
      (queue rows without history) — treated as mediated to avoid false
      "skipped" claims. */
  mediated?: boolean;
}

export function deriveCaseStage(status: CaseStatus, hints: StageHints = {}): CaseStage {
  const stalled = status === 'STALLED';
  let index = STATUS_STAGE[status] ?? 0;
  if (stalled) {
    const from = hints.stalledFrom ? STATUS_STAGE[hints.stalledFrom] : undefined;
    // Without history, Negotiation is the most common stall origin.
    index = from !== undefined && from >= 0 ? from : 2;
  }

  const sublabel = stalled
    ? 'stalled'
    : status === 'ESCALATING'
      ? 'scheduling'
      : status === 'MEDIATING'
        ? 'in session'
        : null;

  return {
    index,
    label: STAGE_LABELS[index],
    sublabel,
    stalled,
    mediationSkipped: index >= 4 && hints.mediated === false,
  };
}

/** Mine stage hints from a case's audit trail (any order). */
export function stageHintsFromAudit(entries: AuditEntry[]): StageHints {
  let stalledFrom: CaseStatus | null = null;
  let mediated = false;
  // Newest-first so the FIRST stall transition we see is the most recent.
  const newestFirst = [...entries].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
  for (const e of newestFirst) {
    if (!stalledFrom && e.new_state === 'STALLED' && e.previous_state) {
      stalledFrom = e.previous_state as CaseStatus;
    }
    if (e.new_state === 'MEDIATING' || e.event_type === 'session.booked') {
      mediated = true;
    }
  }
  return { stalledFrom, mediated };
}
